import { Box, Grid, Stack } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { Suspense } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { InternBrandDashboardJobsQuery } from "./__generated__/InternBrandDashboardJobsQuery.graphql";
import { InternBrandDashboard_brand$key } from "./__generated__/InternBrandDashboard_brand.graphql";
import InternAgreementsCard from "./InternAgreementsCard";
import InternBrandActivityChart from "./InternBrandActivityChart";
import InternBrandStatCards from "./InternBrandStatCards";
import InternIntegrationsCard from "./InternIntegrationsCard";
import InternJobsHealthCard from "./InternJobsHealthCard";
import InternNotificationsCard from "./InternNotificationsCard";
import InternShopifyOrdersCard from "./InternShopifyOrdersCard";

const brandFragment = graphql`
  fragment InternBrandDashboard_brand on BrandType {
    id
    ...InternAgreementsCard_brand
    ...InternNotificationsCard_brand
    ...InternBrandStatCards_brand
    ...InternBrandActivityChart_brand
    ...InternIntegrationsCard_brand
  }
`;

const jobsQuery = graphql`
  query InternBrandDashboardJobsQuery($brandId: String) {
    internJobHealthMetrics(brandId: $brandId) {
      ...InternJobsHealthCard_metrics
    }
  }
`;

type Props = {
  brand: InternBrandDashboard_brand$key;
};

// Helper to decode Relay global ID (base64 encoded "Type:uuid")
const decodeRelayId = (globalId: string): string => {
  try {
    const decoded = atob(globalId);
    // Format is "TypeName:uuid"
    const parts = decoded.split(":");
    return parts.length > 1 ? parts[1] : globalId;
  } catch {
    // If decoding fails, return as-is
    return globalId;
  }
};

// Inner component that fetches jobs data
const JobsHealthSection = ({ brandId }: { brandId: string }) => {
  // Extract the node ID from the relay global ID (base64 encoded)
  const nodeId = decodeRelayId(brandId);

  const data = useLazyLoadQuery<InternBrandDashboardJobsQuery>(jobsQuery, {
    brandId: nodeId,
  });

  if (!data.internJobHealthMetrics) {
    return null;
  }

  return <InternJobsHealthCard metrics={data.internJobHealthMetrics} />;
};

const InternBrandDashboard = ({ brand: brandKey }: Props) => {
  const brand = useFragment(brandFragment, brandKey);

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      {/* Stat Cards Row */}
      <Box sx={{ mb: 3 }}>
        <InternBrandStatCards brand={brand} />
      </Box>

      {/* Charts Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <InternBrandActivityChart brand={brand} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Suspense fallback={null}>
            <InternShopifyOrdersCard brandId={brand.id} />
          </Suspense>
        </Grid>
      </Grid>

      {/* Existing Cards Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <InternNotificationsCard brand={brand} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Stack spacing={2}>
            <InternAgreementsCard brand={brand} />
            <InternIntegrationsCard brand={brand} />
          </Stack>
        </Grid>
      </Grid>

      {/* Jobs Health Row */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Suspense fallback={null}>
            <JobsHealthSection brandId={brand.id} />
          </Suspense>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InternBrandDashboard;
